<template>
    <section
        class="vizy-blocks-content-builder-gallery"
        :data-count="enabledImages?.length || 3"
    >
        <template v-if="pending">
            <Skeletor
                v-for="i in [1,2,3]"
                :key="i"
                class="vizy-blocks-content-builder-gallery__image"
                :shimmer="false"
            />
        </template>

        <template
            v-for="(item, index) in enabledImages"
            v-else
            :key="index"
        >
            <BaseImage
                :image="item.imageGalleryImage?.[0]"
                class="vizy-blocks-content-builder-gallery__image"
                :sizes="imageSizes[index]"
                :data-last="index === enabledImages.length - 1"
            >
                <template
                    v-if="item.imageGalleryLink && item.imageGalleryLink?.customText"
                    #caption
                >
                    <LazyBaseArrowLink
                        :link="item.imageGalleryLink"
                        :element="CraftLink"
                        data-label-position="right"
                    >
                        {{ item.imageGalleryLink?.customText }}
                    </LazyBaseArrowLink>
                </template>
            </BaseImage>
        </template>

        <aside
            v-if="caption || pending"
            class="vizy-blocks-content-builder-gallery__caption"
        >
            <Skeletor
                v-if="pending"
            />
            <template v-else>
                {{ caption }}
            </template>
        </aside>
    </section>
</template>

<script setup>
import { Skeletor } from 'vue-skeletor';

const props = defineProps({
    pending: {
        type: Boolean,
        default: false
    },
    images: {
        type: Array,
        required: true,
        default: () => []
    },
    caption: {
        type: String,
        required: false,
        default: undefined
    }
});

const enabledImages = computed(() => {
    return props.images?.filter(({ enabled }) => enabled);
});

const CraftLink = resolveComponent('CraftLink');

const imageSizeDesktopMiddle = '418px';
const imageSizeDesktopSide = '275px';
const imageSizeMobileMiddle = '338px';
const imageSizeMobileSide = '166px';
const imageSizes = computed(() => {
    switch (props.images?.length) {
    case 1:
        return [
            `(min-width: 768px) ${imageSizeDesktopMiddle}, ${imageSizeMobileMiddle})`
        ];
    case 2:
        return [
            `(min-width: 768px) ${imageSizeDesktopSide}, ${imageSizeMobileSide})`,
            `(min-width: 768px) ${imageSizeDesktopMiddle}, ${imageSizeMobileMiddle})`
        ];
    case 3:
        return [
            `(min-width: 768px) ${imageSizeDesktopSide}, ${imageSizeMobileSide})`,
            `(min-width: 768px) ${imageSizeDesktopMiddle}, ${imageSizeMobileMiddle})`,
            `(min-width: 768px) ${imageSizeDesktopSide}, ${imageSizeMobileSide})`
        ];
    }
    return [];
});
</script>

<style lang="less">
.vizy-blocks-content-builder-gallery {
    --aspect-ratio-tall: 418/619;
    --aspect-ratio-short: 275/406;

    .base-grid(var(--grid-maxWidth-page, 1));
    grid-template-rows: auto;

    &[data-count='1'] {
        .vizy-blocks-content-builder-gallery__image:nth-child(1) {
            grid-column: left-gutter/third;
            grid-row: 1;

            // .base-image__image {
            //  aspect-ratio: var(--aspect-ratio-tall);
            // }

            @media @q-md-min {
                grid-column: second;
            }
        }
    }

    &[data-count='2'] {
        .vizy-blocks-content-builder-gallery__image:nth-child(1) {
            grid-column: left-gutter/third;
            grid-row: 1;

            .base-image__image {
                aspect-ratio: var(--aspect-ratio-tall);
            }

            @media @q-md-min {
                grid-column: second;
                grid-row: 1;
            }
        }

        .vizy-blocks-content-builder-gallery__image:nth-child(2) {
            grid-column: first/right-side-gutter;
            grid-row: 3;

            // .base-image__image {
            //     aspect-ratio: var(--aspect-ratio-short);
            // }

            @media @q-md-min {
                width: 85%;

                grid-column: third;
                justify-self: end;
                grid-row: 1;
            }
        }
    }


    &[data-count='3'] {
        .vizy-blocks-content-builder-gallery__image:nth-child(1) {
            grid-column: left-gutter/third;
            grid-row: 1;

            // .base-image__image {
            //  aspect-ratio: var(--aspect-ratio-short);
            // }

            @media @q-md-min {
                width: 85%;

                grid-column: first;
            }
        }

        .vizy-blocks-content-builder-gallery__image:nth-child(2) {
            grid-column: first/right-side-gutter;
            grid-row: 3;

            .base-image__image {
                aspect-ratio: var(--aspect-ratio-tall);
            }

            @media @q-md-min {
                grid-column: second;
                grid-row: 1;
            }
        }

        .vizy-blocks-content-builder-gallery__image:nth-child(3) {
            grid-column: left-gutter/third;
            grid-row: 4;

            .base-image__image {
                aspect-ratio: var(--aspect-ratio-short);
            }

            @media @q-md-min {
                width: 85%;

                grid-column: third;
                justify-self: end;
                grid-row: 1;
            }
        }
    }
}

.vizy-blocks-content-builder-gallery__image {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--spacing-xl);

    :not[data-count='1'] &[data-last='true'] {
        margin-bottom: 0;
    }

    .base-image {
        flex: 0 1 auto;
    }

    .base-image__caption {
        &, & .base-arrow-link {
            .typography-label();
            text-transform: uppercase;
            white-space: nowrap;
        }
        flex: 1 1 auto;
    }

    .base-image__image {
        height: auto;
    }

    @media @q-md-min {
        margin-bottom: 0;
    }
}

.vizy-blocks-content-builder-gallery__caption {
    .typography-text-sm();

    grid-column: second/right-side-gutter;
    grid-row: 2;
    justify-self: end;

    margin-bottom: var(--spacing-xl);

    text-align: right;
    text-wrap: balance;

    @media @q-md-min {
        grid-column: third;
        grid-row: 1;
        align-self: end;
        margin-bottom: 0;
    }
}
</style>
